/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable sort-keys */
import { UserProfile } from "../interfaces/userProfile.interface";
import { OrgChartNode } from "@interfaces/orgChartNode.interface";

export function	buildOrgChart(users: UserProfile[]): OrgChartNode[] {
	// Create a map for quick user lookup by UserId
	const userMap = new Map<number, UserProfile>();
	users.forEach(user => {
		userMap.set(user.userId, user);
	});

	// Create a map to store children for each supervisor
	const childrenMap = new Map<number, UserProfile[]>();
    
	// Array to store root users (those without supervisors)
	const rootUsers: UserProfile[] = [];

	// Organize users by their supervisor
	users.forEach(user => {
		if (user.supervisorId === null) {
			// This is a root user (no supervisor)
			rootUsers.push(user);
		} else {
			// This user has a supervisor, add them to their supervisor's children
			if (!childrenMap.has(user.supervisorId)) {
				childrenMap.set(user.supervisorId, []);
			}
        childrenMap.get(user.supervisorId)!.push(user);
		}
	});

	// Recursively build the tree structure starting from root users
	const buildNode = (user: UserProfile, level = 0): OrgChartNode => {
		const children = childrenMap.get(user.userId) || [];
      
		return {
			user: user,
			children: children
				.map(child => buildNode(child, level + 1))
				.sort((a, b) => a.user.fullName.localeCompare(b.user.fullName)), // Sort children alphabetically
			level: level
		};
	};

	// Build and return the complete tree structure
	return rootUsers
		.map(user => buildNode(user))
		.sort((a, b) => a.user.fullName.localeCompare(b.user.fullName)); // Sort root nodes alphabetically
}

/**
   * Finds a specific user in the org chart tree
   * @param nodes Array of org chart nodes to search
   * @param userId The user ID to find
   * @returns The node containing the user, or null if not found
   */
export function findUserNode(this: any, nodes: OrgChartNode[], userId: number): OrgChartNode | null {
	for (const node of nodes) {
		if (node.user.userId === userId) {
			return node;
		}
      
		const foundInChildren = this.findUserNode(node.children, userId);
		if (foundInChildren) {
			return foundInChildren;
		}
	}
	return null;
}

/**
   * Gets all users at a specific level in the hierarchy
   * @param nodes Array of org chart nodes
   * @param targetLevel The level to retrieve users from
   * @returns Array of users at the specified level
   */
export function getUsersByLevel(nodes: OrgChartNode[], targetLevel: number): UserProfile[] {
	const users: UserProfile[] = [];
    
	const traverse = (node: OrgChartNode) => {
		if (node.level === targetLevel) {
			users.push(node.user);
		}
		node.children.forEach(child => traverse(child));
	};
    
	nodes.forEach(node => traverse(node));
	return users;
}

/**
   * Gets the total count of users in the org chart
   * @param nodes Array of org chart nodes
   * @returns Total number of users
   */
export function getTotalUserCount(nodes: OrgChartNode[]): number {
	let count = 0;
    
	const traverse = (node: OrgChartNode) => {
		count++;
		node.children.forEach(child => traverse(child));
	};
    
	nodes.forEach(node => traverse(node));
	return count;
}

/**
   * Gets the direct reports for a specific user
   * @param nodes Array of org chart nodes
   * @param userId The supervisor's user ID
   * @returns Array of direct reports
   */
export function getDirectReports(this: any, nodes: OrgChartNode[], userId: number): UserProfile[] {
	const userNode = this.findUserNode(nodes, userId);
	return userNode ? userNode.children.map((child: { user: any; }) => child.user) : [];
}